import LegalLayout, { LegalSection, LegalList } from "@/pages/legal/LegalLayout";

const TOC = [
  { id: "acceptance", label: "Acceptance of terms" },
  { id: "eligibility", label: "User eligibility" },
  { id: "account", label: "Account responsibilities" },
  { id: "use", label: "Permitted & prohibited use" },
  { id: "content", label: "Your content" },
  { id: "ip", label: "Relyn intellectual property" },
  { id: "leo", label: "Leo & AI guidance" },
  { id: "not-medical", label: "Not medical, legal, or emergency" },
  { id: "payment", label: "Subscriptions and Payment" },
  { id: "termination", label: "Suspension & termination" },
  { id: "liability", label: "Limitation of liability" },
  { id: "changes", label: "Changes to service & terms" },
  { id: "law", label: "Governing Law" },
  { id: "contact", label: "Contact" },
];

export default function TermsOfService() {
  return (
    <LegalLayout
      testid="terms-page"
      title="Terms of Service"
      intro="These Terms of Service govern your use of Relyn. Please read them carefully. By using Relyn, you agree to these Terms."
      effectiveDate="July 11, 2026"
      lastUpdated="July 11, 2026"
      toc={TOC}
    >
      <LegalSection id="acceptance" title="Acceptance of the terms">
        <p>
          By creating an account, downloading, installing, or using the Relyn website,
          mobile applications, or related services (collectively, the "Service"), you
          agree to be bound by these Terms of Service and our Privacy Policy. If you do
          not agree, do not use the Service.
        </p>
      </LegalSection>

      <LegalSection id="eligibility" title="User eligibility">
        <p>
          You must be at least 18 years old to use Relyn. By using the Service, you
          represent that you meet this age requirement, have the legal capacity to enter
          into a binding contract, and are not prohibited from using the Service under
          applicable law.
        </p>
      </LegalSection>

      <LegalSection id="account" title="Account responsibilities">
        <LegalList
          items={[
            "You are responsible for maintaining the confidentiality of your account credentials.",
            "You are responsible for all activity that occurs under your account.",
            "You agree to provide accurate, current, and complete information when creating your account.",
            "You must promptly notify us of any suspected unauthorized access to your account by emailing support@relynapp.com.",
          ]}
        />
      </LegalSection>

      <LegalSection id="use" title="Permitted and prohibited use">
        <p>You may use Relyn for personal, non-commercial reflection and growth. You agree not to:</p>
        <LegalList
          items={[
            "Use the Service in any way that violates applicable law or these Terms.",
            "Attempt to reverse engineer, decompile, or extract source code from the Service.",
            "Interfere with, disrupt, or circumvent the security or integrity of the Service.",
            "Use the Service to harass, threaten, defame, or harm others.",
            "Use automated systems to access the Service other than as we expressly permit.",
            "Impersonate any person or entity, or misrepresent your affiliation with any person or entity.",
            "Use the Service to generate content that is illegal, exploitative, or violates the rights of others.",
          ]}
        />
      </LegalSection>

      <LegalSection id="content" title="Ownership of your content">
        <p>
          You retain all rights to the content you create in Relyn — your journal
          entries, mood logs, goals, relationship notes, and conversations with Leo.
          You grant Relyn a limited, worldwide, non-exclusive license to host, process,
          and display your content solely to operate the Service on your behalf. This
          license ends when you delete your content or your account.
        </p>
      </LegalSection>

      <LegalSection id="ip" title="Relyn intellectual property">
        <p>
          Relyn, the Relyn logo, Leo, and all other trademarks, service marks, product
          names, and features of the Service are owned by Relyn or its licensors. The
          software, design, structure, and content of the Service (excluding your own
          user content) are protected by copyright, trademark, and other laws.
        </p>
        <p>
          Except as expressly permitted in these Terms, you may not copy, modify,
          distribute, sell, or lease any part of the Service.
        </p>
      </LegalSection>

      <LegalSection id="leo" title="AI-generated guidance">
        <p>
          Leo is an AI-powered companion that provides general self-awareness and
          relationship-support guidance. Leo's responses are generated by machine
          learning models and may be imperfect, incomplete, or inaccurate.
        </p>
        <LegalList
          items={[
            "Leo is not a licensed therapist, counselor, medical professional, attorney, or emergency service.",
            "Leo's guidance is intended for general reflection and personal growth — not clinical treatment or professional advice.",
            "You should use your own judgment when acting on any suggestion or insight Leo provides.",
          ]}
        />
      </LegalSection>

      <LegalSection id="not-medical" title="Not a medical, mental-health, legal, or emergency service">
        <p>
          <strong>Relyn is not a substitute for professional care.</strong> The Service
          does not provide medical, mental-health, psychiatric, legal, or emergency
          services and should never be relied upon as such.
        </p>
        <p>
          If you are experiencing an emergency, are in immediate danger, or are having
          thoughts of harming yourself or someone else, contact local emergency services
          immediately. In the United States, call 911. For mental-health or
          suicide-related crisis support in the United States, call or text 988. If you
          are outside the United States, contact the emergency or crisis service
          available in your location.
        </p>
      </LegalSection>

      <LegalSection id="payment" title="Subscriptions and Payment">
        <p>
          Relyn may introduce optional paid subscriptions, premium features, trials, or
          other purchases in the future. Before completing a purchase, users will be
          shown the applicable price, billing frequency, renewal terms, cancellation
          options, and any applicable refund terms.
        </p>
        <p>
          If recurring subscriptions are introduced, users will be informed before
          purchase whether the subscription renews automatically and how it may be
          canceled. Additional payment terms may be provided at checkout and through
          the applicable app store or payment provider.
        </p>
      </LegalSection>

      <LegalSection id="termination" title="Account suspension and termination">
        <p>
          We may suspend or terminate your access to the Service, in whole or in part,
          at any time, with or without notice, if we reasonably believe you have violated
          these Terms, the rights of others, or applicable law. You may terminate your
          account at any time from your account settings or by contacting us.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, Relyn and its affiliates, officers,
          employees, and licensors will not be liable for any indirect, incidental,
          special, consequential, or punitive damages, or any loss of profits, revenues,
          or data, arising out of or related to your use of the Service.
        </p>
        <p>
          The Service is provided on an "as is" and "as available" basis without
          warranties of any kind, express or implied, including warranties of
          merchantability, fitness for a particular purpose, or non-infringement.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to the service and terms">
        <p>
          We may modify the Service or these Terms from time to time. When we make
          material changes, we will update the "Last updated" date and notify you where
          appropriate. Your continued use of the Service after changes take effect
          constitutes acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection id="law" title="Governing Law">
        <p>
          These Terms are governed by the laws of the State of Maryland, United States,
          without regard to its conflict-of-laws principles. Any disputes arising from
          or relating to these Terms will be handled in accordance with applicable law.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact">
        <p>
          For questions about these Terms of Service or other legal inquiries, please
          contact us at{" "}
          <a
            href="mailto:support@relynapp.com"
            className="text-[#5aa8ff] hover:text-white underline decoration-[#2D8CFF]/40 underline-offset-4"
            data-testid="legal-contact-email"
          >
            support@relynapp.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
